import { roundHalfUpCents } from '../money';
import type { PrintSheetHtml, PrintSheetLayout } from '../print/templates';
import type { PaymentStatusValue, RefundStatusValue } from './statuses';

/**
 * Payment, allocation, deposit and refund rules as pure functions (piece 22;
 * decisions 0032, 0217, 0239, 0382): the open rows of one Payment never sum
 * past its amount, a dissociation closes a row rather than deleting a payment,
 * an unallocated Received payment is a customer deposit and an unallocated
 * Sent one a vendor prepayment, and a refund off the original rail names a
 * reason and an authoriser.
 */

export type AllocationSourceKind = 'Payment' | 'VendorCredit' | 'CreditNote';
export type AllocationTargetKind = 'Invoice' | 'Bill' | 'CreditNote' | 'ExpenseReport';

export interface OpenAllocation {
  amount: number;
  closedAt: Date | null;
}

/** The open rows only; a closed row counts toward no sum and is never deleted (decision 0382). */
export function openTotal(rows: readonly OpenAllocation[]): number {
  return rows.filter((row) => row.closedAt === null).reduce((sum, row) => sum + row.amount, 0);
}

/** 🔨 The open rows sourced from one Payment sum to no more than that Payment's amount; the allocation that would carry them past it is refused at write. */
export function allocationProblem(input: { sourceAmount: number; openAlready: number; adding: number; targetOpenBalance: number }): string | null {
  if (!Number.isFinite(input.adding) || input.adding <= 0) return 'an allocation applies an amount above zero';
  if (input.openAlready + input.adding > input.sourceAmount) return 'the allocation would carry this payment past its own amount; dissociate a row or allocate less';
  if (input.adding > input.targetOpenBalance) return 'the allocation is more than the document still owes';
  return null;
}

/** A payment with nothing allocated is available credit, never a refused payment: Received sits as a customer deposit, Sent as a vendor prepayment. */
export function unallocatedRemainder(sourceAmount: number, openAlready: number): number {
  return Math.max(0, sourceAmount - openAlready);
}

/**
 * The pro-rata split of a payment across several open invoices, by open
 * balance, rounded half-up with the residual cents on the largest balance so
 * the parts sum to the payment exactly (piece 23, partial payments allocate
 * proportionally).
 */
export function proRata(amount: number, targets: readonly { id: string; openBalance: number }[]): { id: string; amount: number }[] {
  const open = targets.filter((t) => t.openBalance > 0);
  const total = open.reduce((sum, t) => sum + t.openBalance, 0);
  if (total <= 0 || amount <= 0) return [];
  // A payment at or past the total settles each in full; nothing is pro-rated where nothing is short.
  if (amount >= total) return open.map((t) => ({ id: t.id, amount: t.openBalance }));
  const shares = open.map((t) => ({ id: t.id, openBalance: t.openBalance, amount: roundHalfUpCents((amount * t.openBalance) / total) }));
  const residual = amount - shares.reduce((sum, s) => sum + s.amount, 0);
  if (residual !== 0) {
    const largest = shares.reduce((best, s) => (s.openBalance > best.openBalance ? s : best), shares[0]!);
    largest.amount += residual;
  }
  return shares.filter((s) => s.amount > 0).map((s) => ({ id: s.id, amount: s.amount }));
}

/**
 * 🔒 The default a payment recorded from the Payments list opens on (T132;
 * decision 0710): the amount applied to the account's open invoices oldest
 * first, each taking what it still owes until the money runs out. What is left
 * over is applied to nothing and sits as the account's available credit, the
 * deposit route piece 22 already describes. The caller passes the invoices in
 * the order they are to be settled; a written-off one is left out, because
 * reversing a write-off is a named act against a named invoice.
 */
export function oldestFirst(amount: number, targets: readonly { id: string; openBalance: number }[]): { id: string; amount: number }[] {
  const out: { id: string; amount: number }[] = [];
  let left = Math.max(0, amount);
  for (const target of targets) {
    if (left <= 0) break;
    if (target.openBalance <= 0) continue;
    const take = Math.min(left, target.openBalance);
    out.push({ id: target.id, amount: take });
    left -= take;
  }
  return out;
}

/** 🔨 `appliedOn` is written at allocation from the source's own date and never edited after. */
export function appliedOnFrom(source: { paymentDate?: Date | null; creditDate?: Date | null }): Date {
  const date = source.paymentDate ?? source.creditDate;
  if (!date) throw new Error('an allocation reads its date from the Payment’s paymentDate or the credit’s creditDate');
  return date;
}

/** Dissociating a mis-applied allocation closes the row and leaves the Payment standing; a closed row is never closed twice. */
export function dissociateProblem(row: { closedAt: Date | null }): string | null {
  return row.closedAt === null ? null : 'the allocation is already dissociated';
}

/** A payment is recorded before it is allocated, and a void payment allocates nothing. */
export function paymentAllocatableProblem(status: PaymentStatusValue): string | null {
  if (status === 'Void') return 'a void payment allocates nothing';
  return null;
}

/**
 * 🔨 `reason` and `authorisedByPersonId` are required when the refund's rail
 * is not the original's, so the ledger can report refunds by method and name
 * who authorised the off-rail ones (decision 0032).
 */
export function refundProblem(input: {
  paymentMethod: 'Check' | 'ACH' | 'Card' | 'Cash' | 'Other';
  refundMethod: 'Gateway' | 'Check' | 'ACH';
  amount: number;
  paymentAmount: number;
  alreadyRefunded: number;
  reason?: string | null;
  authorisedByPersonId?: string | null;
}): string | null {
  if (!Number.isFinite(input.amount) || input.amount <= 0) return 'a refund returns an amount above zero';
  if (input.amount + input.alreadyRefunded > input.paymentAmount) return 'the refunds against this payment would return more than it brought in';
  if (!offOriginalRail(input.paymentMethod, input.refundMethod)) return null;
  if (!input.reason?.trim()) return 'a refund on a rail other than the original names its reason; type why this refund does not go back the way the money came in';
  if (!input.authorisedByPersonId) return 'a refund on a rail other than the original names who authorized it; choose who authorized it';
  return null;
}

export interface RefundDrawRow {
  id: string;
  amount: number;
}

export interface RefundDraw {
  /** Taken off the payment's unallocated remainder, which closes nothing. */
  fromUnallocated: number;
  /** Each open allocation the refund draws down, with what is left of it after. */
  reductions: { id: string; drawn: number; remaining: number }[];
}

/**
 * 🔨 A refund gives back money the payment already applied, so it draws what
 * the payment still holds unallocated first and then reduces the open
 * allocations pro rata, the same split the allocation helper uses (found while
 * building, 2026-09-15). What it still holds counts the earlier refunds out:
 * money already given back is not available to give back twice. A row drawn to
 * nothing closes; a row drawn in part closes and is rewritten for what is
 * left, because an allocation's amount is written once (decision 0382).
 */
export function refundDraw(refundCents: number, payment: { amount: number; alreadyRefunded?: number }, open: readonly RefundDrawRow[]): RefundDraw {
  const openAlready = open.reduce((sum, row) => sum + row.amount, 0);
  const fromUnallocated = Math.min(Math.max(0, refundCents), unallocatedRemainder(payment.amount, openAlready + Math.max(0, payment.alreadyRefunded ?? 0)));
  const toDraw = Math.min(Math.max(0, refundCents - fromUnallocated), openAlready);
  if (toDraw <= 0) return { fromUnallocated, reductions: [] };
  const shares = proRata(toDraw, open.map((row) => ({ id: row.id, openBalance: row.amount })));
  const reductions = shares.map((share) => {
    const row = open.find((candidate) => candidate.id === share.id)!;
    return { id: share.id, drawn: share.amount, remaining: row.amount - share.amount };
  });
  return { fromUnallocated, reductions };
}

/** Whether the refund leaves by a different rail from the one the money came in on; a failed gateway attempt becoming a printed check is the case. */
export function offOriginalRail(paymentMethod: string, refundMethod: 'Gateway' | 'Check' | 'ACH'): boolean {
  if (refundMethod === 'Gateway') return paymentMethod !== 'Card';
  return refundMethod !== paymentMethod;
}

/** A refund is its own row and can happen more than once; a failed attempt is a second row, never an edit. */
export function refundRetryProblem(status: RefundStatusValue): string | null {
  return status === 'Failed' || status === 'Void' ? null : 'a refund still open is completed or failed before another is raised on it';
}

/** 🔨 A deposit groups received payments only, and a banked deposit takes no more. */
export function depositAddProblem(input: { depositStatus: 'Open' | 'Deposited' | 'Void'; direction: 'Received' | 'Sent' }): string | null {
  if (input.direction !== 'Received') return 'a deposit groups received payments; a payment sent is not banked';
  if (input.depositStatus !== 'Open') return `a ${input.depositStatus} deposit takes no more payments`;
  return null;
}

/** The deposit's amount is the sum of the payments in it, surcharge included, because that is the one line the bank statement shows. */
export function depositAmount(payments: readonly { amount: number; surchargeAmount: number | null }[]): number {
  return payments.reduce((sum, p) => sum + p.amount + (p.surchargeAmount ?? 0), 0);
}

/** 🔨 A payment carrying a refund is not voided; the refund is voided first, so the money given back is never orphaned. */
export const REFUND_BLOCKS_VOID = 'this payment carries a refund; void the refund before the payment';

export interface PaymentFooter {
  /** Apply what is left across the account's open invoices; absent once nothing is left to apply. */
  mayAllocate: boolean;
  mayRefund: boolean;
  mayVoid: boolean;
  /** Why the acts that are absent are absent, in the server's own words. */
  refusal: string | null;
}

/**
 * The Payment page's acts per status (piece 22, the payment's screens). 🔨 The
 * page draws what the status allows and the server refuses on its own terms
 * whatever the page draws, so the two answers come from this one function.
 * There is no Record here and no Edit: a payment is recorded once and its
 * money is corrected by dissociating, refunding or voiding.
 */
export function paymentFooter(facts: { status: PaymentStatusValue; unallocatedCents: number; amountCents: number; refundedCents: number; openRefunds: number }): PaymentFooter {
  const allocatable = paymentAllocatableProblem(facts.status);
  const voided = facts.status === 'Void';
  return {
    mayAllocate: allocatable === null && facts.unallocatedCents > 0,
    mayRefund: !voided && facts.refundedCents < facts.amountCents,
    mayVoid: !voided && facts.openRefunds === 0,
    refusal: allocatable ?? (!voided && facts.openRefunds > 0 ? REFUND_BLOCKS_VOID : null),
  };
}

/**
 * 🔒 The Payment as its own routes answer it, declared once so the service that
 * builds it and the screen that reads it are the same shape (piece 22;
 * decision 0409).
 */
export interface AllocationRow {
  id: string;
  targetType: string;
  targetId: string;
  targetNumber: string | null;
  amountCents: number;
  appliedOn: string;
  closedAt: string | null;
}

/** One refund against the payment, as the page's Refunds block reads it. */
export interface RefundRow {
  id: string;
  refundedOn: string;
  method: string;
  amountCents: number;
  status: string;
  reason: string | null;
}

export interface PaymentAnswer {
  id: string;
  /** 🔒 The layout the sheet prints on and the sheet itself, as the printer's own HTML (decision 0567; T169). */
  printLayout?: PrintSheetLayout | null;
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
  /**
   * 🔒 T3126 (decision 7630): true where the sheet is the payment's check, drawn
   * over the check stock on the screen alone, and false where it is the receipt.
   */
  checkSheet?: boolean;
  companyId: string;
  companyName: string;
  direction: string;
  method: string;
  reference: string | null;
  status: string;
  paymentDate: string;
  currencyCode: string;
  amountCents: number;
  surchargeAmountCents: number | null;
  /**
   * 🔒 The account the money moved through (decision 0434): the payment's own
   * on the Sent side, and the account of the Deposit it was banked in on the
   * Received side. 🔨 An unbanked receipt reads null and raises no flag —
   * recording a cheque before the deposit is made is an ordinary state.
   */
  bankAccountName: string | null;
  allocatedCents: number;
  /** 🔨 An unallocated remainder is the account's available credit, never a refused payment. */
  unallocatedCents: number;
  depositId: string | null;
  refundedCents: number;
  allocations: AllocationRow[];
  refunds: RefundRow[];
  /** The three acts the status allows, decided once here so the page and the server agree (decision 0409). */
  footer: PaymentFooter;
}
